import { useMemo } from "react";
import { useSelector } from "react-redux";

// hooks and utilities
import searchForTab from "common/searchTab";

// assets
import tabs from "assets/json/tabs.json";

function useTabId(path, isNumber = true) {
	const address = useSelector(state => state.window.currentAddress);

	const id = useMemo(() => {
		if (address == null) return;

		const splittedPath = path.split(".");
		const splittedAddress = address.split(".");

		// ie. redirect("") -> redirects to the top of the tree
		if (splittedPath.length === 1 && splittedPath[0] === "") {
			getIdFromPathFragment(splittedAddress[0], path);
			return;
		}

		// Not that deep in the tree -> null is returned
		if (splittedAddress.length < splittedPath.length) {
			return null;
		}

		while (splittedPath.length > 0) {
			if (splittedPath[0] !== splittedAddress[0] && splittedPath[0] !== "*" && !splittedAddress[0].includes("~")) {
				return null;
			}

			splittedPath.shift();
			splittedPath.length !== 0 && splittedAddress.shift();
		}

		return getIdFromPathFragment(splittedAddress[0], path);

		function getIdFromPathFragment(pathFragment, path) {
			const tabInfo = searchForTab(tabs, path);
			if (!tabInfo?.needsId) {
				console.error(tabInfo);
				throw new Error(
					"Tab at provided path doesn't require ID to be present. Either change the path or update tabs.json file."
				);
			}

			const res = pathFragment.split("~")[1];

			if (res == null && +tabInfo?.needsId !== 2) {
				throw new Error("An id was expected to be contained in the address but is not!");
			}

			if (res == null) return null;
			return isNumber ? +res : res;
		}
	}, [path, address, isNumber]);

	if (path.includes("*")) {
		throw new Error(
			"The useTabId hook doesn't support the wildcard ('*')! Replace it with the real path before calling the hook."
		);
	}

	return id;
}

function useTabIdNew(path, { isNumber = true, ...options }) {
	const address = useSelector(options.addressState);

	const id = useMemo(() => {
		if (address == null) return;

		const splittedPath = path.split(".");
		const splittedAddress = address.split(".");

		// ie. redirect("") -> redirects to the top of the tree
		if (splittedPath.length === 1 && splittedPath[0] === "") {
			getIdFromPathFragment(splittedAddress[0], path);
			return;
		}

		// Not that deep in the tree -> null is returned
		if (splittedAddress.length < splittedPath.length) {
			return null;
		}

		while (splittedPath.length > 0) {
			if (splittedPath[0] !== splittedAddress[0] && splittedPath[0] !== "*" && !splittedAddress[0].includes("~")) {
				return null;
			}

			splittedPath.shift();
			splittedPath.length !== 0 && splittedAddress.shift();
		}

		return getIdFromPathFragment(splittedAddress[0], path);

		function getIdFromPathFragment(pathFragment, path) {
			const tabInfo = searchForTab(options.tabs, path);
			if (!tabInfo?.needsId) {
				console.error(tabInfo);
				throw new Error(
					"Tab at provided path doesn't require ID to be present. Either change the path or update tabs.json file."
				);
			}

			const res = pathFragment.split("~")[1];

			if (res == null && +tabInfo?.needsId !== 2) {
				throw new Error("An id was expected to be contained in the address but is not!");
			}

			if (res == null) return null;
			return isNumber ? +res : res;
		}
	}, [path, address, isNumber, options.tabs]);

	if (path.includes("*")) {
		throw new Error(
			"The useTabId hook doesn't support the wildcard ('*')! Replace it with the real path before calling the hook."
		);
	}

	return id;
}

export { useTabIdNew };
export default useTabId;
